import {
  BAND_CONTENT,
  DIMENSION_LABEL,
  DIMENSION_ORDER,
  dimensionLevel,
  type ScoreResult,
} from './scoring'

export type ScoreResultPayload = ScoreResult

const MAX_QUERY_LENGTH = 4_000

export function createScoreResultUrl(result: ScoreResult): string {
  const serialized = encodeScoreResult(result)
  return `/score?result=${encodeURIComponent(serialized)}`
}

export function encodeScoreResult(result: ScoreResult): string {
  const json = JSON.stringify(result)
  const bytes = new TextEncoder().encode(json)
  let binary = ''

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })

  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '')
}

export function decodeScoreResult(search: string): ScoreResultPayload | null {
  const value = new URLSearchParams(search).get('result')

  if (!value || value.length > MAX_QUERY_LENGTH) {
    return null
  }

  try {
    const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
    const binary = atob(padded)
    const bytes = Uint8Array.from(binary, (character) =>
      character.charCodeAt(0),
    )
    const parsed: unknown = JSON.parse(new TextDecoder().decode(bytes))

    return isScoreResultPayload(parsed) ? parsed : null
  } catch {
    return null
  }
}

export function createAuditHref(result: ScoreResult): string {
  const dimensionSummary = DIMENSION_ORDER.map(
    (dimension) =>
      `${DIMENSION_LABEL[dimension]}: ${result.dimensions[dimension]} / 6`,
  ).join('\n')
  const content = BAND_CONTENT[result.band]
  const subject = `Reader Path Audit request — ${result.totalScore} / 24`
  const body = [
    'Hi John,',
    '',
    "I'd like to explore a Reader Path Audit.",
    '',
    'My Reader-to-Revenue Score',
    `Total: ${result.totalScore} / 24`,
    `Result: ${result.band}`,
    `Biggest opportunity: ${DIMENSION_LABEL[result.biggestOpportunity]}`,
    `Strongest layer: ${DIMENSION_LABEL[result.strongestLayer]}`,
    '',
    'Dimension scores',
    dimensionSummary,
    '',
    'What this means',
    content.meaning,
    '',
    'Thank you,',
  ].join('\n')

  return `mailto:john@growththatholds.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

function isScoreResultPayload(value: unknown): value is ScoreResultPayload {
  if (!value || typeof value !== 'object') {
    return false
  }

  const result = value as Record<string, unknown>
  const dimensionKeys = new Set(DIMENSION_ORDER)

  if (
    typeof result.totalScore !== 'number' ||
    !Number.isInteger(result.totalScore) ||
    result.totalScore < 0 ||
    result.totalScore > 24 ||
    typeof result.band !== 'string' ||
    !Object.prototype.hasOwnProperty.call(BAND_CONTENT, result.band) ||
    typeof result.biggestOpportunity !== 'string' ||
    !dimensionKeys.has(
      result.biggestOpportunity as ScoreResult['biggestOpportunity'],
    ) ||
    typeof result.strongestLayer !== 'string' ||
    !dimensionKeys.has(
      result.strongestLayer as ScoreResult['strongestLayer'],
    ) ||
    !result.dimensions ||
    typeof result.dimensions !== 'object' ||
    Array.isArray(result.dimensions)
  ) {
    return false
  }

  const dimensions = result.dimensions as Record<string, unknown>
  const total = DIMENSION_ORDER.reduce((sum, dimension) => {
    const score = dimensions[dimension]

    return typeof score === 'number' &&
      Number.isInteger(score) &&
      score >= 0 &&
      score <= 6
      ? sum + score
      : Number.NaN
  }, 0)

  return Number.isFinite(total) && total === result.totalScore
}
